import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Button,
    Table,
    DatePicker,
    Space,
    Typography,
    Modal,
    message,
    Statistic,
    Divider,
    Tabs
} from 'antd';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line
} from 'recharts';
import {
    UserOutlined,
    ShoppingOutlined,
    DollarOutlined,
    DeleteOutlined,
    EyeOutlined,
    FileTextOutlined,
    CalendarOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import moment from 'moment';
import reportService from '../services/reportService';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import PageHeader from '../components/Common/PageHeader';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const Reports = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([moment().subtract(6, 'months'), moment()]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        loadReports();
    }, []);

    const loadReports = async () => {
        try {
            setLoading(true);
            const response = await reportService.getReports();
            if (response.status === 'success') {
                setReports(response.data.data);
            }
        } catch (error) {
            message.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReport = async (type) => {
        try {
            setLoading(true);
            const [startDate, endDate] = dateRange;
            const params = {
                start_date: startDate.format('YYYY-MM-DD'),
                end_date: endDate.format('YYYY-MM-DD')
            };

            let response;
            switch (type) {
                case 'user':
                    response = await reportService.generateUserReport(params);
                    break;
                case 'auction':
                    response = await reportService.generateAuctionReport(params);
                    break;
                case 'bid':
                    response = await reportService.generateBidReport(params);
                    break;
                default:
                    throw new Error('Invalid report type');
            }

            if (response.status === 'success') {
                message.success('Report generated successfully');
                loadReports();
            }
        } catch (error) {
            message.error('Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const handleViewReport = async (report) => {
        try {
            setLoading(true);
            const response = await reportService.getReport(report.id);
            if (response.status === 'success') {
                setSelectedReport(response.data);
                setModalVisible(true);
            }
        } catch (error) {
            message.error('Failed to load report details');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteReport = async (reportId) => {
        try {
            setLoading(true);
            const response = await reportService.deleteReport(reportId);
            if (response.status === 'success') {
                message.success('Report deleted successfully');
                loadReports();
            }
        } catch (error) {
            message.error('Failed to delete report');
        } finally {
            setLoading(false);
        }
    };

    const renderReportContent = () => {
        if (!selectedReport) return null;

        const { type, data } = selectedReport;

        switch (type) {
            case 'user_activity':
                return (
                    <>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Card>
                                    <Statistic
                                        title="Total Users"
                                        value={data.total_users}
                                        prefix={<UserOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col span={8}>
                                <Card>
                                    <Statistic
                                        title="New Users"
                                        value={data.new_users}
                                        prefix={<UserOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col span={8}>
                                <Card>
                                    <Statistic
                                        title="Active Users"
                                        value={data.active_users}
                                        prefix={<UserOutlined />}
                                    />
                                </Card>
                            </Col>
                        </Row>
                        <Divider />
                        <Title level={4}>Top Active Users</Title>
                        <Table
                            dataSource={data.user_activity}
                            columns={[
                                { title: 'Name', dataIndex: 'name' },
                                { title: 'Email', dataIndex: 'email' },
                                { title: 'Bids Count', dataIndex: 'bids_count' }
                            ]}
                            rowKey="id"
                        />
                    </>
                );

            case 'auction_performance':
                return (
                    <>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Total Auctions"
                                        value={data.total_auctions}
                                        prefix={<ShoppingOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Active Auctions"
                                        value={data.active_auctions}
                                        prefix={<ShoppingOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Total Revenue"
                                        value={data.auction_stats.total_revenue}
                                        prefix={<DollarOutlined />}
                                        precision={2}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Avg Bids per Lot"
                                        value={data.auction_stats.average_bids_per_lot}
                                        precision={2}
                                    />
                                </Card>
                            </Col>
                        </Row>
                        <Divider />
                        <Title level={4}>Top Auctions</Title>
                        <Table
                            dataSource={data.top_auctions}
                            columns={[
                                { title: 'Title', dataIndex: 'title' },
                                { title: 'Lots Count', dataIndex: 'lots_count' },
                                { title: 'Bids Count', dataIndex: 'bids_count' },
                                { title: 'Total Value', dataIndex: 'total_value', render: value => `$${value}` }
                            ]}
                            rowKey="id"
                        />
                    </>
                );

            case 'bid_analysis':
                return (
                    <>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Total Bids"
                                        value={data.total_bids}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Total Value"
                                        value={data.total_value}
                                        prefix={<DollarOutlined />}
                                        precision={2}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Average Bid"
                                        value={data.average_bid_amount}
                                        prefix={<DollarOutlined />}
                                        precision={2}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Winning Bids"
                                        value={data.winning_bids}
                                    />
                                </Card>
                            </Col>
                        </Row>
                        <Divider />
                        <Title level={4}>Bid Distribution</Title>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={data.bid_distribution}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="count" stroke="#8884d8" />
                            </LineChart>
                        </ResponsiveContainer>
                        <Divider />
                        <Title level={4}>Top Bidders</Title>
                        <Table
                            dataSource={data.top_bidders}
                            columns={[
                                { title: 'Name', dataIndex: 'name' },
                                { title: 'Bids Count', dataIndex: 'bids_count' },
                                { title: 'Total Amount', dataIndex: 'total_amount', render: value => `$${value}` }
                            ]}
                            rowKey="id"
                        />
                    </>
                );

            default:
                return null;
        }
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => {
                const types = {
                    user_activity: 'User Activity',
                    auction_performance: 'Auction Performance',
                    bid_analysis: 'Bid Analysis'
                };
                return types[type] || type;
            }
        },
        {
            title: 'Generated At',
            dataIndex: 'generated_at',
            key: 'generated_at',
            render: (date) => moment(date).format('YYYY-MM-DD HH:mm')
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => handleViewReport(record)}
                    >
                        {/* View */}
                    </Button>
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteReport(record.id)}
                    >
                        {/* Delete */}
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <DashboardLayout>
            <PageHeader
                title="Reports"
                description="Generate and manage analytical reports for users, auctions, and bids"
            />

            <Card 
                style={{ 
                    marginBottom: 24,
                    borderRadius: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    border: '1px solid #f0f0f0'
                }}
            >
                <div style={{ marginBottom: 20 }}>
                    <Space align="center">
                        <FileTextOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                            Generate New Report
                        </Title>
                    </Space>
                    <Typography.Text type="secondary" style={{ fontSize: '14px', marginLeft: 32, display: 'block', marginTop: 4 }}>
                        Select a date range and choose the type of report you want to generate
                    </Typography.Text>
                </div>
                
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={24} md={8} lg={6}>
                        <div style={{ marginBottom: 8 }}>
                            <Typography.Text strong style={{ fontSize: '13px', color: '#595959' }}>
                                Date Range
                            </Typography.Text>
                        </div>
                        <RangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            style={{ width: '100%' }}
                            size="large"
                            format="YYYY-MM-DD"
                            suffixIcon={<CalendarOutlined />}
                        />
                    </Col>
                    <Col xs={24} sm={24} md={16} lg={18}>
                        <div style={{ marginBottom: 8 }}>
                            <Typography.Text strong style={{ fontSize: '13px', color: '#595959' }}>
                                Report Types
                            </Typography.Text>
                        </div>
                        <Space wrap size="middle">
                            <Button
                                type="primary"
                                icon={<UserOutlined />}
                                onClick={() => handleGenerateReport('user')}
                                loading={loading}
                                size="large"
                                style={{
                                    height: 40,
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
                                }}
                            >
                                Generate User Report
                            </Button>
                            <Button
                                type="primary"
                                icon={<ShoppingOutlined />}
                                onClick={() => handleGenerateReport('auction')}
                                loading={loading}
                                size="large"
                                style={{
                                    height: 40,
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
                                }}
                            >
                                Generate Auction Report
                            </Button>
                            <Button
                                type="primary"
                                icon={<BarChartOutlined />}
                                onClick={() => handleGenerateReport('bid')}
                                loading={loading}
                                size="large"
                                style={{
                                    height: 40,
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
                                }}
                            >
                                Generate Bid Report
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

                <div style={{ marginBottom: 16 }}>
                    <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                        Generated Reports
                    </Title>
                    <Typography.Text type="secondary" style={{ fontSize: '13px', display: 'block', marginTop: 4 }}>
                        View and manage all generated reports
                    </Typography.Text>
                </div>
                <Table
                    scroll={{ x: 1000 }}
                    columns={columns}
                    dataSource={reports}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showTotal: (total) => `Total ${total} reports`
                    }}
                />

            <Modal
                title={selectedReport?.title}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                width={1000}
                footer={null}
            >
                {renderReportContent()}
            </Modal>
        </DashboardLayout>
    );
};

export default Reports; 